import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';

// ── Eager (layout / guard) imports ────────────────────────────────────────────
import { MarketingLayout, MarketingProtectedRoute } from './features/marketing/components';
import { MarketingRole } from './features/marketing/types';
import PlatformLayout from './features/platform/components/PlatformLayout';
import { useReferralCapture } from './features/marketing/hooks/useReferralCapture';
import { useMarketingAuthStore } from './store/marketingAuthStore';

// ── Route fallback ─────────────────────────────────────────────────────────────
import { RouteFallback } from './components/RouteFallback';

// ── Lazy page imports — marketing realm ───────────────────────────────────────
const LandingPage              = lazy(() => import('./pages/landing/LandingPage'));
const PrivacyPage              = lazy(() => import('./pages/legal/PrivacyPage'));
const TermsPage                = lazy(() => import('./pages/legal/TermsPage'));
const DataDeletionPage         = lazy(() => import('./pages/legal/DataDeletionPage'));
const DataDeletionStatusPage   = lazy(() => import('./pages/legal/DataDeletionStatusPage'));
const MarketingLoginPage       = lazy(() => import('./pages/marketing/MarketingLoginPage'));
const RegisterWorkspacePage    = lazy(() => import('./pages/marketing/RegisterWorkspacePage'));
const AcceptInvitePage         = lazy(() => import('./pages/marketing/AcceptInvitePage'));
const ForgotPasswordPage       = lazy(() => import('./pages/marketing/ForgotPasswordPage'));
const ResetPasswordPage        = lazy(() => import('./pages/marketing/ResetPasswordPage'));
const WidgetChatPage           = lazy(() => import('./pages/marketing/WidgetChatPage'));
const CommandCenterPage        = lazy(() => import('./pages/marketing/command/CommandCenterPage'));
const MarketingDashboardPage   = lazy(() => import('./pages/marketing/MarketingDashboardPage'));
// The merged surface (spec §1). Serves BOTH /inbox and /leads; the leads list
// is lazy-loaded inside it as the Kişiler tab, so it is no longer imported
// here.
const InboxPage                = lazy(() => import('./pages/marketing/inbox/InboxPage'));
const CreateLeadPage           = lazy(() => import('./pages/marketing/CreateLeadPage'));
const LeadDetailPage           = lazy(() => import('./pages/marketing/leadDetail/LeadDetailPage'));
const TasksPage                = lazy(() => import('./pages/marketing/tasks/TasksPage'));
const CalendarPage             = lazy(() => import('./pages/marketing/calendar/CalendarPage'));
const OpportunitiesPage        = lazy(() => import('./pages/marketing/opportunities/OpportunitiesPage'));
// Offers + Estimates + Documents are TABS of the Documents hub now.
const DocumentsHubPage         = lazy(() => import('./pages/marketing/documents/DocumentsHubPage'));
// Ads / Performance / Analytics are TABS inside ReportsPage now.
const ReportsPage              = lazy(() => import('./pages/marketing/ReportsPage'));
const GrowthStudioPage         = lazy(() => import('./pages/marketing/studio/GrowthStudioPage'));
const StrategyOnboarding       = lazy(() => import('./pages/marketing/strategy/StrategyOnboarding'));
const HelpPage                 = lazy(() => import('./pages/marketing/help'));
const CompaniesPage            = lazy(() => import('./pages/marketing/companies'));
const EmailTemplatesPage       = lazy(() => import('./pages/marketing/emailTemplates'));
const CommissionsPage          = lazy(() => import('./pages/marketing/CommissionsPage'));
const InstallationsPage        = lazy(() => import('./pages/marketing/installations/InstallationsPage'));
const ProspectingPage          = lazy(() => import('./pages/marketing/ProspectingPage'));
// Manager-only pages
const AccountCenterPage        = lazy(() => import('./pages/marketing/accounts/AccountCenterPage'));
const CoursesPage              = lazy(() => import('./pages/marketing/memberships/courses'));
const CourseEditorPage         = lazy(() => import('./pages/marketing/memberships/courses/CourseEditorPage'));
const AffiliatePortalPage      = lazy(() => import('./pages/marketing/affiliate-portal/AffiliatePortalPage'));
const AgencyLocationsPage      = lazy(() => import('./pages/marketing/agency/LocationsPage'));
const AgencySnapshotsPage      = lazy(() => import('./pages/marketing/agency/SnapshotsPage'));
const AgencyRebillingPage      = lazy(() => import('./pages/marketing/agency/RebillingPage'));
const ResearchSuggestionsPage  = lazy(() => import('./pages/marketing/research/ResearchSuggestionsPage'));
// Channels / Canned Responses / AI Agents / Knowledge are TABS of /inbox now.
// SIX PAIRS THAT WERE ONE JOB EACH, now one page each with deep-linkable tabs.
// The old paths still resolve — they redirect here with their tab set — so the
// LIST got shorter without anything becoming unreachable.
const DomainsPage              = lazy(() => import('./pages/marketing/settings/DomainsPage'));
const WebhooksHubPage          = lazy(() => import('./pages/marketing/settings/WebhooksHubPage'));
const ApiAccessPage            = lazy(() => import('./pages/marketing/settings/ApiAccessPage'));
const TeamPage                 = lazy(() => import('./pages/marketing/TeamPage'));
const VoiceHubPage             = lazy(() => import('./pages/marketing/VoiceHubPage'));
// 2026-09-04: the second settings pass. Strategy owns the machinery that serves
// it; selling is one page in the order a sale happens; the plan owns the switch
// that turns features off. Every absorbed route below redirects to its tab.
const GrowthEnginePage         = lazy(() => import('./pages/marketing/strategy/GrowthEnginePage'));
const SellingPage              = lazy(() => import('./pages/marketing/products/SellingPage'));
const PlanAndAccessPage        = lazy(() => import('./pages/marketing/billing/PlanAndAccessPage'));
const TriggerLinksPage         = lazy(() => import('./pages/marketing/triggerLinks'));
// Tax Rates + Coupons are TABS of /products now.
const AutomationBuilderPage    = lazy(() => import('./pages/marketing/automations/AutomationBuilderPage'));
const SitesPage                = lazy(() => import('./pages/marketing/SitesPage'));
const AppointmentsPage         = lazy(() => import('./pages/marketing/appointments/AppointmentsPage'));
const PublicBookingPage        = lazy(() => import('./pages/marketing/appointments/PublicBookingPage'));
const ReviewsPage              = lazy(() => import('./pages/marketing/ReviewsPage'));
const BrandingSettingsPage     = lazy(() => import('./pages/marketing/BrandingSettingsPage'));
// GHL-parity settings/tools UIs
// MCP connector console (Faz 4) — the operator mirror of the MCP surface:
// endpoint address, write mode, connected clients, session audit.
const AiModelsPage             = lazy(() => import('./pages/marketing/settings/aiModels'));
// Settings→Connections is the Integrations TAB of the Account Center now.
const TwoFactorPage            = lazy(() => import('./pages/marketing/settings/twoFactor'));
// MCP OAuth consent (Faz 3) — the SPA route `GET /api/mcp-oauth/authorize`
// 302s the browser to. Path is fixed by MCP_OAUTH_CONSENT_PAGE_PATH on the
// backend; changing it here alone silently breaks the connector flow.
const McpConsentPage           = lazy(() => import('./pages/marketing/oauth/McpConsentPage'));
const CompliancePage           = lazy(() => import('./pages/marketing/settings/compliance'));
// AI Studio + UGC Personas live in Growth Studio's Create tab; Brand Kit and
// Brand Brain are tabs of the ONE Brand page (/branding).
const SocialCampaignDetailPage = lazy(() => import('./pages/marketing/socialCampaigns/SocialCampaignDetailPage'));
const AffiliatesPage           = lazy(() => import('./pages/marketing/experiments/affiliates'));

// ── Lazy page imports — platform (superadmin) realm ───────────────────────────
const PlatformLoginPage          = lazy(() => import('./pages/platform/PlatformLoginPage'));
const PlatformWorkspacesPage     = lazy(() => import('./pages/platform/PlatformWorkspacesPage'));
const PlatformWorkspaceDetailPage = lazy(() => import('./pages/platform/PlatformWorkspaceDetailPage'));
const ManualPaymentsPage         = lazy(() => import('./pages/platform/ManualPaymentsPage'));
const PlatformRoutinesPage       = lazy(() => import('./pages/platform/routines/PlatformRoutinesPage'));

// ── Dev-only lazy page ────────────────────────────────────────────────────────
const UiKitchenSinkPage = lazy(() => import('./pages/_dev/UiKitchenSinkPage'));

// Helper: wrap a lazy page in a Suspense boundary with the shared fallback.
function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

/**
 * The person-primary surface's routes, as DATA rather than as JSX buried in the
 * table below — so the unit suite can assert the wiring that actually ships.
 *
 * PersonSurface.test.tsx used to build its own two-line route table and assert
 * against that. A hand-copy proves the copy: deleting `defaultTab="contacts"`
 * from this file once left every one of those tests green while `/leads` opened
 * on the wrong tab, and only a browser noticed.
 *
 * Both paths stay — they are members of the frozen 50-path set
 * (navigation.test.ts) and they are in people's bookmarks. They now render the
 * SAME component with no prop between them: after the 2026-08-29 correction
 * there are no tabs left to differ about, only one list of people. `?tab=`
 * still reaches the four config surfaces from either path.
 */
export const MERGED_SURFACE_ROUTES: { path: string; element: React.ReactElement }[] = [
  { path: '/inbox', element: <S><InboxPage /></S> },
  { path: '/leads', element: <S><InboxPage /></S> },
];

// Unknown paths: signed-in users keep deep-link recovery into the app; everyone
// else lands on the public marketing home rather than being bounced to /login.
function CatchAllRedirect() {
  const isAuthenticated = useMarketingAuthStore((s) => s.isAuthenticated);
  return <Navigate to={isAuthenticated ? '/home' : '/'} replace />;
}

/**
 * Redirect that PRESERVES the current query string and force-sets extras.
 * Needed where a BACKEND OAuth callback 302s the browser to a legacy path with
 * result params (e.g. /ads?connect=… → /reports?tab=ads&connect=…) — a plain
 * <Navigate to> would drop them and the post-OAuth dialog would never open.
 */
function RedirectMergingParams({ to, set }: { to: string; set: Record<string, string> }) {
  const [params] = useSearchParams();
  const merged = new URLSearchParams(params);
  for (const [k, v] of Object.entries(set)) merged.set(k, v);
  return <Navigate to={`${to}?${merged.toString()}`} replace />;
}

/**
 * Standalone marketing console served at the ROOT of its own (sub)domain. This is
 * the sole home of the marketing panel now — the POS app no longer embeds it
 * (its /marketing routes were removed; nginx 301-redirects /marketing/* here).
 */
export default function App() {
  // Capture `?ref=CODE` into a 30-day cookie once, regardless of landing route.
  // NOTE: cookie CONSUMPTION on register/checkout (reading the cookie back and
  // attaching the code to the create-workspace / order payload) is a separate
  // backend-contract task — not wired here.
  useReferralCapture();

  return (
    <Routes>
      {/* Public marketing home (Jeeta landing). Stays public even when signed
          in — the nav surfaces an "Open app" CTA in that case. */}
      <Route path="/"         element={<S><LandingPage /></S>} />
      {/* Public legal pages — linked from the landing footer. */}
      <Route path="/privacy"  element={<S><PrivacyPage /></S>} />
      <Route path="/terms"    element={<S><TermsPage /></S>} />
      {/* Data deletion: the instructions page platform reviews (Meta, TikTok,
          Pinterest, LinkedIn) ask for, and the status page the `url` we return
          from Meta's deletion callback points at. Both public, no session. */}
      <Route path="/data-deletion"        element={<S><DataDeletionPage /></S>} />
      <Route path="/data-deletion-status" element={<S><DataDeletionStatusPage /></S>} />
      <Route path="/login"    element={<S><MarketingLoginPage /></S>} />
      <Route path="/register" element={<S><RegisterWorkspacePage /></S>} />
      {/* Public accept-invite landing — the invite TOKEN in the query string
          is the only credential (Task 15); no auth guard, unlike login/register. */}
      <Route path="/accept-invite" element={<S><AcceptInvitePage /></S>} />
      {/* Password recovery. Both public, and both must stay outside
          MarketingProtectedRoute: having no session is the problem being
          solved. `/reset-password` is the exact path the mailed link points
          at (`${FRONTEND_URL}/reset-password?token=…`), so the catch-all
          below must never be the one that answers it — it redirects to the
          landing page and drops the query string with the token in it. */}
      <Route path="/forgot-password" element={<S><ForgotPasswordPage /></S>} />
      <Route path="/reset-password"  element={<S><ResetPasswordPage /></S>} />
      {/* Public web-chat surface — embedded in an iframe by widget.js. */}
      <Route path="/widget"   element={<S><WidgetChatPage /></S>} />
      {/* Public self-service booking page (no auth). */}
      <Route path="/book/:ws/:cal" element={<S><PublicBookingPage /></S>} />
      {/* Standalone affiliate self-serve portal (token-authenticated, no session). */}
      <Route path="/affiliate-portal" element={<S><AffiliatePortalPage /></S>} />

      {/* Platform (superadmin) realm — separate auth store + token. The login
          page sits OUTSIDE the layout; PlatformLayout carries the realm auth
          guard (redirects to /platform/login when unauthenticated) + shell. */}
      <Route path="/platform/login" element={<S><PlatformLoginPage /></S>} />
      <Route element={<PlatformLayout />}>
        <Route path="/platform/workspaces"    element={<S><PlatformWorkspacesPage /></S>} />
        <Route path="/platform/workspaces/:id" element={<S><PlatformWorkspaceDetailPage /></S>} />
        <Route path="/platform/payments"      element={<S><ManualPaymentsPage /></S>} />
        <Route path="/platform/routines"      element={<S><PlatformRoutinesPage /></S>} />
      </Route>

      {/* Marketing realm — guarded by MarketingProtectedRoute (auth check). */}
      <Route element={<MarketingProtectedRoute />}>
        {/* MCP OAuth consent — auth-guarded (so an unauthenticated arrival gets
            the login screen and is returned here via ?next=) but deliberately
            OUTSIDE MarketingLayout: it is a full-screen decision, not a page of
            the app, and the surrounding nav would invite navigating away
            mid-authorization, leaving the connector waiting forever. */}
        <Route path="/oauth/consent" element={<S><McpConsentPage /></S>} />
        <Route element={<MarketingLayout />}>
          {/* The home screen. `/dashboard` stays a real route — the KPI board is
              still useful and is linked from here — but it is no longer where
              someone lands, because landing on metrics implies you are the one
              who has to act on them. */}
          <Route path="/home"      element={<S><CommandCenterPage /></S>} />
          <Route path="/dashboard" element={<S><MarketingDashboardPage /></S>} />
          {/* One surface, two routes, no difference between them — see
              MERGED_SURFACE_ROUTES above. */}
          {MERGED_SURFACE_ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          <Route path="/leads/new" element={<S><CreateLeadPage /></S>} />
          <Route path="/leads/:id" element={<S><LeadDetailPage /></S>} />
          <Route path="/leads/:id/edit" element={<S><CreateLeadPage /></S>} />
          <Route path="/companies" element={<S><CompaniesPage /></S>} />
          <Route path="/tasks"          element={<S><TasksPage /></S>} />
          <Route path="/calendar"       element={<S><CalendarPage /></S>} />
          <Route path="/opportunities"  element={<S><OpportunitiesPage /></S>} />
          {/* Offers | Estimates | Documents — ONE hub, tabs at ?tab= */}
          <Route path="/documents"      element={<S><DocumentsHubPage /></S>} />
          {/* Overview | Ads | Performance | Analytics — ONE page, tabs at ?tab= */}
          <Route path="/reports"        element={<S><ReportsPage /></S>} />
          {/* Backend ads-OAuth callbacks 302 to /ads?connect=… — params must survive. */}
          <Route path="/ads"            element={<RedirectMergingParams to="/reports" set={{ tab: 'ads' }} />} />
          <Route path="/performance"    element={<RedirectMergingParams to="/reports" set={{ tab: 'performance' }} />} />
          <Route path="/analytics"      element={<RedirectMergingParams to="/reports" set={{ tab: 'analytics' }} />} />
          {/* Growth Studio is Autopilot-first: /studio IS the autopilot console;
              the manual tools live behind ?view=tools&tab=… */}
          <Route path="/studio"         element={<S><GrowthStudioPage /></S>} />
          {/* AI Strategy Engine (Task 9): the console + its first-run onboarding
              wizard. Both live under the standard marketing auth guard. */}
          <Route path="/studio/strategy"   element={<S><GrowthEnginePage /></S>} />
          <Route path="/onboarding/strategy" element={<S><StrategyOnboarding /></S>} />
          {/*
            `/budget` is the one saved address that ever named the Growth
            Autopilot console, and until 2026-09-01 landing on /studio WAS
            landing on the console. It is a panel behind a drawer now, so the
            redirect has to name it — otherwise the bookmark quietly becomes
            "somewhere near it", and the console holds the kill switch for an
            engine that spends money.
          */}
          <Route path="/budget"         element={<Navigate to="/studio?tool=autopilot" replace />} />
          <Route path="/trends"         element={<Navigate to="/studio?view=tools&tab=trends" replace />} />
          <Route path="/content-calendar" element={<Navigate to="/studio?view=tools&tab=calendar" replace />} />
          <Route path="/commissions"    element={<S><CommissionsPage /></S>} />
          <Route path="/installations"  element={<S><InstallationsPage /></S>} />
          {/* Calls | Power Dialer — ONE page, tabs at ?tab= */}
          <Route path="/calls"          element={<RedirectMergingParams to="/voice" set={{ tab: 'calls' }} />} />
          <Route path="/prospecting"    element={<S><ProspectingPage /></S>} />
          <Route path="/billing"        element={<S><PlanAndAccessPage /></S>} />
          {/* In-app help center (connection guides) — available to everyone. */}
          <Route path="/help"           element={<S><HelpPage /></S>} />
          <Route path="/help/:slug"     element={<S><HelpPage /></S>} />
          {/* Self-service 2FA — available to every authenticated marketing user. */}
          <Route path="/settings/two-factor" element={<S><TwoFactorPage /></S>} />
        </Route>
        <Route element={<MarketingProtectedRoute requiredRole={MarketingRole.MANAGER} />}>
          <Route element={<MarketingLayout />}>
            <Route path="/users"       element={<S><TeamPage /></S>} />
            <Route path="/targets"     element={<RedirectMergingParams to="/users" set={{ tab: 'targets' }} />} />
            <Route path="/accounts"    element={<S><AccountCenterPage /></S>} />
            <Route path="/settings/business-types" element={<RedirectMergingParams to="/inbox" set={{ tab: 'types' }} />} />
            <Route path="/settings/custom-fields" element={<RedirectMergingParams to="/inbox" set={{ tab: 'fields' }} />} />
            <Route path="/settings/pipelines" element={<RedirectMergingParams to="/branding" set={{ tab: 'pipelines' }} />} />
            <Route path="/tags" element={<RedirectMergingParams to="/inbox" set={{ tab: 'audience', sub: 'tags' }} />} />
            <Route path="/settings/tags" element={<Navigate to="/tags" replace />} />
            <Route path="/segments" element={<RedirectMergingParams to="/inbox" set={{ tab: 'audience' }} />} />
            <Route path="/settings/segments" element={<Navigate to="/segments" replace />} />
            <Route path="/import" element={<RedirectMergingParams to="/inbox" set={{ tab: 'import' }} />} />
            <Route path="/settings/import" element={<Navigate to="/import" replace />} />
            <Route path="/memberships/courses"            element={<S><CoursesPage /></S>} />
            <Route path="/memberships/courses/:id"        element={<S><CourseEditorPage /></S>} />
            {/* Agency console (Epic D) — each page self-guards on workspace.kind === AGENCY
                (AgencyGuard); backend additionally 403s every /agency route for non-agencies. */}
            <Route path="/agency/locations"  element={<S><AgencyLocationsPage /></S>} />
            <Route path="/agency/snapshots"  element={<S><AgencySnapshotsPage /></S>} />
            <Route path="/agency/rebilling"  element={<S><AgencyRebillingPage /></S>} />
            <Route path="/research"    element={<RedirectMergingParams to="/studio/strategy" set={{ tab: 'research' }} />} />
            <Route path="/research/suggestions" element={<S><ResearchSuggestionsPage /></S>} />
            {/* Channels / Canned Responses / AI Agents / Knowledge → /inbox?tab=… */}
            <Route path="/settings/domains" element={<S><DomainsPage /></S>} />
            <Route path="/settings/sending-domains" element={<RedirectMergingParams to="/settings/domains" set={{ tab: 'sending' }} />} />
            <Route path="/settings/custom-domains" element={<RedirectMergingParams to="/settings/domains" set={{ tab: 'custom' }} />} />
            {/* Tax Rates + Coupons → /products?tab=… */}
            <Route path="/automations" element={<RedirectMergingParams to="/studio/strategy" set={{ tab: 'automations' }} />} />
            <Route path="/automations/new" element={<S><AutomationBuilderPage /></S>} />
            <Route path="/automations/:id/edit" element={<S><AutomationBuilderPage /></S>} />
            <Route path="/campaigns"   element={<Navigate to="/studio?view=tools&tab=campaigns" replace />} />
            <Route path="/email-templates" element={<S><EmailTemplatesPage /></S>} />
            <Route path="/sites"       element={<S><SitesPage /></S>} />
            <Route path="/booking"     element={<RedirectMergingParams to="/users" set={{ tab: 'booking' }} />} />
            <Route path="/appointments" element={<S><AppointmentsPage /></S>} />
            <Route path="/reviews"     element={<S><ReviewsPage /></S>} />
            <Route path="/voice"       element={<S><VoiceHubPage /></S>} />
            <Route path="/invoices"    element={<RedirectMergingParams to="/products" set={{ tab: 'invoices' }} />} />
            <Route path="/products"    element={<S><SellingPage /></S>} />
            <Route path="/subscriptions" element={<RedirectMergingParams to="/products" set={{ tab: 'subscriptions' }} />} />
            <Route path="/order-forms"   element={<RedirectMergingParams to="/products" set={{ tab: 'order-forms' }} />} />
            {/* Business | Brand Kit | Brand Brain — ONE Brand page, tabs at ?tab= */}
            <Route path="/branding"    element={<S><BrandingSettingsPage /></S>} />
            {/* GHL-parity settings/tools UIs (manager-gated; server-side OWNER/MANAGER). */}
            <Route path="/settings/api-keys"    element={<S><ApiAccessPage /></S>} />
            <Route path="/settings/modules"     element={<RedirectMergingParams to="/billing" set={{ tab: 'modules' }} />} />
            <Route path="/settings/webhooks"    element={<S><WebhooksHubPage /></S>} />
            <Route path="/settings/inbound-webhooks" element={<RedirectMergingParams to="/settings/webhooks" set={{ tab: 'inbound' }} />} />
            {/* MCP connector console (Faz 4) — MANAGER-readable; the write-mode
                switch inside self-disables from overview.canToggle (OWNER-only). */}
            <Route path="/settings/mcp-console" element={<RedirectMergingParams to="/settings/api-keys" set={{ tab: 'connector' }} />} />
            {/* Which fal.ai model this workspace generates on, with each one's
                price. MANAGER-gated here, matching the PATCH's own
                MANAGER + settings.manage floor. */}
            <Route path="/settings/ai-models"   element={<S><AiModelsPage /></S>} />
            {/* Backend calendar-OAuth callbacks 302 to /settings/connections?gcal=… —
                params must survive into the Account Center's Integrations tab. */}
            <Route path="/settings/connections" element={<RedirectMergingParams to="/accounts" set={{ tab: 'integrations' }} />} />
            <Route path="/settings/roles"       element={<RedirectMergingParams to="/users" set={{ tab: 'roles' }} />} />
            <Route path="/settings/compliance"  element={<S><CompliancePage /></S>} />
            {/* Telephony + Voice-AI setup now lives in the Account Center. */}
            <Route path="/settings/telephony"   element={<Navigate to="/accounts" replace />} />
            <Route path="/settings/voice-ai"    element={<Navigate to="/accounts" replace />} />
            {/* Each redirect names the SUB-tab it means. Without one they both
                landed on blast Campaigns — a page neither URL is about — while
                GrowthStudioPage's own comment claimed all six carried an exact
                tab/sub pair. */}
            <Route path="/social"      element={<Navigate to="/studio?view=tools&tab=campaigns&sub=planner" replace />} />
            <Route path="/social-campaigns"      element={<Navigate to="/studio?view=tools&tab=campaigns&sub=social" replace />} />
            <Route path="/social-campaigns/:id"  element={<S><SocialCampaignDetailPage /></S>} />
            <Route path="/trigger-links" element={<S><TriggerLinksPage /></S>} />
            <Route path="/voice/ivr"   element={<RedirectMergingParams to="/voice" set={{ tab: 'ivr' }} />} />
            <Route path="/affiliates"  element={<S><AffiliatesPage /></S>} />
          </Route>
        </Route>
      </Route>

      {import.meta.env.DEV && (
        <Route
          path="/_dev/ui"
          element={
            <Suspense fallback={null}>
              <UiKitchenSinkPage />
            </Suspense>
          }
        />
      )}
      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  );
}
